import { useAuth } from "@/src/contexts/auth.context";
import {
  saveToken,
  useResetPasswordMutation,
  useSetPasswordMutation,
} from "@/src/services/api/auth.api";
import { generateStrongPassword } from "@/src/utils/form-validators";
import { zodResolver } from "@hookform/resolvers/zod";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { z } from "zod";

// How long the "Account activated" state shows before the redirect to
// /login, so the click-through from the email doesn't feel like it did
// nothing before bouncing away.
const ACTIVATED_REDIRECT_DELAY_MS = 1800;

const resetPasswordSchema = z
  .object({
    password: z
      .string({
        required_error: "ERROR_NO_PASSWORD",
      })
      .min(1, { message: "ERROR_NO_PASSWORD" })
      .min(8, { message: "ERROR_PASSWORD_LENGTH" }),
    confirmPassword: z
      .string({
        required_error: "ERROR_PASSWORD_CONFIRM",
      })
      .min(1, { message: "ERROR_PASSWORD_CONFIRM" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "ERROR_PASSWORD_NOT_MATCH",
    path: ["confirmPassword"],
  });

export type ResetPasswordForm = Readonly<z.infer<typeof resetPasswordSchema>>;

type PasswordScreenType = "reset-password" | "set-password";

/**
 * Pull the human-readable reason out of a failed reset/activate response.
 *
 * v4 reports field errors as `{isSuccess: false, errors: {Code: "The code has
 * expired…"}}` (verified — see BLOCKS_ENDPOINTS in services/blocks/config.ts).
 * There is no `message` property on these responses, so reading only
 * `data.message` fell through to the fallback on EVERY failure and the actual
 * reason never reached the user.
 *
 * Values are unwrapped with `flat()` because the field maps to either a bare
 * string (what these two endpoints send) or the array-of-strings that the same
 * platform uses elsewhere; taking the first non-empty string covers both.
 *
 * `data.message` is kept in the chain after it so a route that does send one is
 * still preferred over the generic fallback.
 */
const extractPasswordError = (
  error: any,
  t: (key: string) => string,
): string => {
  const data = error?.response?.data;
  const fieldError =
    data?.errors && typeof data.errors === "object"
      ? Object.values(data.errors)
          .flat()
          .find((value) => typeof value === "string" && value.trim())
      : undefined;

  return (
    (fieldError as string | undefined) ||
    data?.message ||
    t("ERROR_RESET_LINK_INVALID")
  );
};

export const useResetPassword = (
  type: PasswordScreenType = "reset-password",
) => {
  const [formError, setFormError] = useState<string | undefined>();
  const [mounted, setMounted] = useState(false);
  const [activated, setActivated] = useState(false);
  const { t } = useTranslation();
  const { logout } = useAuth();
  const params = useLocalSearchParams();
  const { code } = params;

  useEffect(() => {
    setMounted(true);
  }, []);

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const password = watch("password");

  const resetPasswordMutation = useResetPasswordMutation({
    onSuccess: async () => {
      Toast.show({
        type: "success",
        text1: t("SUCCESS_PASSWORD_RESET"),
      });
      // The reset is sent with `logoutFromAllDevices: true` (auth.api.ts), so every
      // existing session — including the one this screen may still be holding — is
      // already revoked server-side. `logout()` is what makes the client agree.
      //
      // A bare `router.replace("/login")` left `isAuthenticated` true, so
      // AuthProvider's redirect effect saw an ordinary route and forwarded /login
      // straight on to /feeds, into a session the server had just invalidated.
      //
      // The response's `access_token` is deliberately not persisted. On web
      // `saveToken` is a no-op and the HttpOnly cookie is the session, so it never
      // did anything; on native it would outlive this logout — `logout()` does not
      // call `clearTokens` — and silently re-authenticate on next launch, defeating
      // the revoke the user just asked for.
      await logout();
    },
    onError: (error: any) => {
      setFormError(extractPasswordError(error, t));
    },
  });

  const setPasswordMutation = useSetPasswordMutation({
    // Deliberately still the original save-token-then-replace path, NOT the
    // `logout()` used by reset-password above. Activation is opened by a brand-new
    // user with no session, so the /feeds bounce that motivated the change there
    // does not occur here, and this code is already in production. Left alone on
    // purpose — see the reset-password handler for the reasoning that would apply
    // if activation ever starts running against a live session.
    onSuccess: async (data) => {
      await saveToken(data.access_token);
      setActivated(true);
    },
    onError: (error: any) => {
      setFormError(extractPasswordError(error, t));
    },
  });

  // Invited accounts already got a real, generated password at creation (see
  // inviteUser in users.blocks.api.ts), so there's nothing left for the person
  // opening this link to type — activation just needs the code redeemed once.
  //
  // Redeeming it used to fire automatically on mount, i.e. on a bare page GET.
  // That is exactly what corporate mail security (Outlook Safe Links, Defender
  // for Office 365, and equivalents) pre-fetches: it visits every link in an
  // incoming email to scan it BEFORE the recipient opens the email at all. The
  // code is single-use, so the scanner's silent request burned it — the real
  // person's later click then hit "the code has expired" seconds after the
  // invite went out, even though the account was fine because the scanner's
  // own redemption happened to succeed. That "happened to" is the problem: if
  // the scanner's request fails for any unrelated reason (different egress IP,
  // a WAF, a timeout), the code is still burned but the account never
  // activates, and the real person is stuck with no way in.
  //
  // A non-interactive URL scanner fetches the page; it does not execute JS and
  // click a button. So the fix is to require exactly that — redemption now
  // waits for `activateAccount()`, called from a real button press — before
  // anything is sent.
  const activationAttempted = useRef(false);

  useEffect(() => {
    if (type !== "set-password" || !mounted) return;
    if (typeof code !== "string" || !code) {
      // A bare /set-password with no code — nothing to redeem, and nothing a
      // click would fix. Surface this immediately rather than showing an
      // "Activate" button for a link that was never going to work.
      setFormError(t("ERROR_RESET_LINK_INVALID"));
    }
  }, [type, mounted, code, t]);

  const activateAccount = () => {
    if (activationAttempted.current) return;
    if (typeof code !== "string" || !code) return;
    activationAttempted.current = true;

    setFormError(undefined);
    setPasswordMutation.mutate({
      password: generateStrongPassword(),
      code,
    });
  };

  // Hold the "Account activated" state on screen briefly before bouncing to
  // /login, so the click-through from the email reads as something having
  // happened rather than a flash before the redirect.
  useEffect(() => {
    if (!activated) return;
    Toast.show({ type: "success", text1: t("SUCCESS_ACCOUNT_ACTIVATED") });
    const timer = setTimeout(() => {
      router.replace("/login");
    }, ACTIVATED_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [activated, t]);

  const onSubmit = (data: ResetPasswordForm) => {
    setFormError(undefined);

    // Only reset-password still collects a typed password — set-password
    // activates via activateAccount(), fired from a button press.
    resetPasswordMutation.mutate({
      password: data.password,
      code: code as string,
    });
  };

  const isSubmitting =
    type === "reset-password"
      ? resetPasswordMutation.isPending
      : setPasswordMutation.isPending;

  return {
    control,
    handleSubmit,
    errors,
    password,

    formError,
    mounted,
    isSubmitting,
    activated,
    activateAccount,
    activating: setPasswordMutation.isPending,

    onSubmit,

    t,
  };
};
